import { Competitor, ComparisonPage } from '@/types/comparison';

export const competitors: Competitor[] = [
  {
    id: 'beautiful-ai',
    name: 'Beautiful.ai',
    slug: 'beautiful-ai',
    tagline: 'Design AI presentation tool',
    color: {
      primary: 'blue-600',
      secondary: 'blue-100',
      gradient: 'from-blue-600 to-cyan-600',
    },
    category: 'presentation-ai',
    website: 'https://www.beautiful.ai',
    pricing: {
      starting: '$12/month',
      model: 'Subscription per user',
    },
  },
  {
    id: 'gamma',
    name: 'Gamma',
    slug: 'gamma',
    tagline: 'AI-powered presentation generator',
    color: {
      primary: 'purple-600',
      secondary: 'purple-100',
      gradient: 'from-purple-600 to-pink-600',
    },
    category: 'presentation-ai',
    website: 'https://gamma.app',
    pricing: {
      starting: '$8/month',
      model: 'Subscription per user',
    },
  },
  {
    id: 'pitch',
    name: 'Pitch',
    slug: 'pitch',
    tagline: 'Collaborative presentation software',
    color: {
      primary: 'pink-600',
      secondary: 'pink-100',
      gradient: 'from-pink-600 to-rose-600',
    },
    category: 'presentation-tool',
    website: 'https://pitch.com',
    pricing: {
      starting: '$8/month',
      model: 'Subscription per user',
    },
  },
  {
    id: 'powerpoint',
    name: 'PowerPoint',
    slug: 'powerpoint',
    tagline: 'Microsoft presentation software',
    color: {
      primary: 'orange-600',
      secondary: 'orange-100',
      gradient: 'from-orange-600 to-red-600',
    },
    category: 'traditional',
    website: 'https://www.microsoft.com/microsoft-365/powerpoint',
    pricing: {
      starting: '$6.99/month',
      model: 'Microsoft 365 subscription',
    },
  },
];

export const comparisonPages: ComparisonPage[] = [
  // Beautiful.ai Comparison
  {
    competitor: competitors[0],
    summary: {
      headline: 'Start from your idea, not a template.',
      subheadline: "Beautiful.ai's templates keep you on-brand. Deckster's agents write and design the slides themselves — so you start from what you want to say.",
      description: 'Beautiful.ai pioneered AI-powered design automation with smart templates. Deckster takes it further with four specialized AI agents that collaborate to create truly custom presentations, not just template variations.',
    },
    keyDifferentiators: [
      {
        id: 'multi-agent',
        title: 'Multi-Agent AI System',
        description: 'Deckster uses four specialized AI agents (Director, Scripter, Graphic Artist, Data Visualizer) working together. Beautiful.ai uses template-based automation with design rules.',
        icon: 'Users',
        advantage: 'deckster',
      },
      {
        id: 'content-generation',
        title: 'Content Generation',
        description: 'Deckster\'s Scripter agent writes compelling narratives and persuasive copy. Beautiful.ai requires you to write most content yourself or use basic AI text generation.',
        icon: 'PenTool',
        advantage: 'deckster',
      },
      {
        id: 'customization',
        title: 'True Customization',
        description: 'Deckster AI creates unique layouts for each slide. Beautiful.ai adapts from a library of smart templates, which can look repetitive across different presentations.',
        icon: 'Sparkles',
        advantage: 'deckster',
      },
      {
        id: 'ease-of-use',
        title: 'Visual Interface',
        description: 'Beautiful.ai offers an excellent drag-and-drop interface that\'s very intuitive. Both platforms are easy to use but in different ways.',
        icon: 'Layout',
        advantage: 'neutral',
      },
    ],
    featureComparison: [
      {
        id: 'ai-content-writing',
        feature: 'AI Content Writing',
        deckster: { value: true, description: 'Full narrative and copywriting', highlight: true },
        competitor: { value: 'Basic', description: 'Simple text generation' },
        category: 'ai',
      },
      {
        id: 'ai-design',
        feature: 'AI Design Automation',
        deckster: { value: true, description: 'Custom AI-generated layouts', highlight: true },
        competitor: { value: true, description: 'Smart template adaptation' },
        category: 'ai',
      },
      {
        id: 'data-viz',
        feature: 'AI Data Visualization',
        deckster: { value: true, description: 'Specialist agent for charts', highlight: true },
        competitor: { value: 'Partial', description: 'Smart chart templates' },
        category: 'ai',
      },
      {
        id: 'templates',
        feature: 'Template Library',
        deckster: { value: '50+', description: 'Starter templates' },
        competitor: { value: '100+', description: 'Smart templates', highlight: true },
        category: 'core',
      },
      {
        id: 'collab',
        feature: 'Real-time Collaboration',
        deckster: { value: true, description: 'Coming soon' },
        competitor: { value: true, description: 'Full collaboration suite', highlight: true },
        category: 'collaboration',
      },
      {
        id: 'export',
        feature: 'Export Options',
        deckster: { value: 'PDF, PPTX', description: 'Standard formats' },
        competitor: { value: 'PDF, PPTX, Video', description: 'Including video export', highlight: true },
        category: 'export',
      },
      {
        id: 'pricing',
        feature: 'Starting Price',
        deckster: { value: '$15/month', description: 'All features included' },
        competitor: { value: '$12/month', description: 'Pro plan', highlight: true },
        category: 'pricing',
      },
    ],
    useCaseComparisons: [
      {
        id: 'sales-deck',
        useCase: 'Creating a Sales Deck from Scratch',
        decksterApproach: 'Describe your product and audience. AI writes persuasive copy, designs slides, and creates compelling visuals - ready in minutes.',
        competitorApproach: 'Choose a sales template, manually write all content, add your data. Beautiful.ai automatically adjusts layout as you type.',
        winner: 'deckster',
      },
      {
        id: 'brand-consistency',
        useCase: 'Maintaining Brand Consistency',
        decksterApproach: 'Upload brand guidelines once. All AI-generated content follows your colors, fonts, and style.',
        competitorApproach: 'Create brand templates with your colors and fonts. Smart templates maintain consistency across slides.',
        winner: 'tie',
      },
      {
        id: 'quick-updates',
        useCase: 'Quick Design Updates',
        decksterApproach: 'Ask AI to redesign specific slides or update styling. Edit inline with preview.',
        competitorApproach: 'Make changes manually. Smart templates auto-adjust layout as you edit content.',
        winner: 'tie',
      },
    ],
    verdict: {
      title: 'Different Tools for Different Needs',
      description: 'Beautiful.ai excels at giving you beautiful templates with smart layout automation. Deckster goes beyond with AI agents that actually create your content and custom designs.',
      bestFor: [
        'Users who want to write their own content',
        'Teams with established content processes',
        'Users who love template-based workflows',
        'Need for video export functionality',
      ],
      decksterBestFor: [
        'Users who want AI to help write content',
        'Creating presentations from scratch quickly',
        'Truly unique, non-template designs',
        'Data-heavy presentations needing smart visualizations',
      ],
    },
  },

  // Gamma Comparison
  {
    competitor: competitors[1],
    summary: {
      headline: 'For decks that go deeper than one prompt.',
      subheadline: "Gamma's single general model is great for a fast first draft. Deckster routes your deck through specialists — research, analysis, design — for work you'll present, not just share.",
      description: 'Gamma uses a single AI model to generate presentations. Deckster employs four specialized agents - each an expert in their domain - collaborating to create higher-quality output.',
    },
    keyDifferentiators: [
      {
        id: 'specialization',
        title: 'AI Specialization',
        description: 'Deckster has dedicated agents for writing (Scripter), design (Graphic Artist), data (Data Visualizer), and coordination (Director). Gamma uses one general-purpose AI for everything.',
        icon: 'Brain',
        advantage: 'deckster',
      },
      {
        id: 'output-quality',
        title: 'Output Quality',
        description: 'Specialized agents produce more refined results in their domains - better copywriting, more sophisticated designs, smarter data visualizations.',
        icon: 'Award',
        advantage: 'deckster',
      },
      {
        id: 'web-first',
        title: 'Web-First Experience',
        description: 'Gamma offers an excellent web-native experience with inline editing and beautiful rendering. Both platforms provide modern web experiences.',
        icon: 'Globe',
        advantage: 'neutral',
      },
      {
        id: 'speed',
        title: 'Generation Speed',
        description: 'Gamma generates presentations very quickly with instant AI generation. Both platforms are fast but optimized differently.',
        icon: 'Zap',
        advantage: 'neutral',
      },
    ],
    featureComparison: [
      {
        id: 'multi-agent-ai',
        feature: 'Multi-Agent AI Architecture',
        deckster: { value: true, description: '4 specialized AI agents', highlight: true },
        competitor: { value: false, description: 'Single AI model' },
        category: 'ai',
      },
      {
        id: 'ai-writing',
        feature: 'AI Copywriting Quality',
        deckster: { value: 'Advanced', description: 'Dedicated Scripter agent', highlight: true },
        competitor: { value: 'Good', description: 'General AI writing' },
        category: 'ai',
      },
      {
        id: 'design-quality',
        feature: 'Design Sophistication',
        deckster: { value: 'Advanced', description: 'Specialist Graphic Artist', highlight: true },
        competitor: { value: 'Good', description: 'AI-generated layouts' },
        category: 'ai',
      },
      {
        id: 'web-editor',
        feature: 'Web-Native Editor',
        deckster: { value: true, description: 'Modern web interface' },
        competitor: { value: true, description: 'Excellent web editor', highlight: true },
        category: 'core',
      },
      {
        id: 'formats',
        feature: 'Content Formats',
        deckster: { value: 'Presentations', description: 'Focused on decks' },
        competitor: { value: 'Presentations, Docs, Webpages', description: 'Multiple formats', highlight: true },
        category: 'core',
      },
      {
        id: 'interactive',
        feature: 'Interactive Elements',
        deckster: { value: 'Basic', description: 'Standard interactions' },
        competitor: { value: true, description: 'Quizzes, forms, embeds', highlight: true },
        category: 'core',
      },
      {
        id: 'analytics',
        feature: 'Presentation Analytics',
        deckster: { value: 'Coming soon', description: 'Planned feature' },
        competitor: { value: true, description: 'View tracking', highlight: true },
        category: 'collaboration',
      },
      {
        id: 'pricing-model',
        feature: 'Starting Price',
        deckster: { value: '$15/month', description: 'Full access' },
        competitor: { value: '$8/month', description: 'Plus plan', highlight: true },
        category: 'pricing',
      },
    ],
    useCaseComparisons: [
      {
        id: 'complex-data',
        useCase: 'Presenting Complex Data',
        decksterApproach: 'Data Visualizer agent analyzes your data, selects optimal chart types, and creates insightful visualizations automatically.',
        competitorApproach: 'AI generates basic charts. You can refine them manually with Gamma\'s editor.',
        winner: 'deckster',
      },
      {
        id: 'pitch-deck',
        useCase: 'Creating an Investor Pitch',
        decksterApproach: 'Director agent structures narrative, Scripter writes persuasive copy, Graphic Artist designs professional slides - all coordinated for maximum impact.',
        competitorApproach: 'Single AI generates full deck quickly. Good quality output that you can refine.',
        winner: 'deckster',
      },
      {
        id: 'sharing',
        useCase: 'Sharing and Tracking',
        decksterApproach: 'Export to PPTX or PDF for sharing. Analytics features coming soon.',
        competitorApproach: 'Share via web link with built-in analytics to track views and engagement.',
        winner: 'competitor',
      },
    ],
    verdict: {
      title: 'Depth vs. Breadth',
      description: 'Gamma is excellent for quick AI-generated presentations across multiple formats. Deckster focuses solely on presentations but uses specialized AI to create superior quality.',
      bestFor: [
        'Need multiple content formats (docs, webpages)',
        'Want built-in analytics and tracking',
        'Budget-conscious teams',
        'Prefer very fast generation',
      ],
      decksterBestFor: [
        'Presentations that need to be exceptional',
        'Complex data visualization needs',
        'Professional pitch decks and sales presentations',
        'Users who value AI specialization over speed',
      ],
    },
  },

  // Pitch Comparison
  {
    competitor: competitors[2],
    summary: {
      headline: 'Built for you first. Collaborate after.',
      subheadline: 'Pitch is built for teams editing together. Deckster builds the deck for you — then you refine it by asking.',
      description: 'Pitch is a collaborative presentation platform designed for teams working together. Deckster is an AI-first platform where agents do the heavy lifting of creation. Different tools for different workflows.',
    },
    keyDifferentiators: [
      {
        id: 'ai-vs-collab',
        title: 'AI Creation vs. Team Collaboration',
        description: 'Deckster AI agents create your presentation content and design. Pitch provides collaboration tools for teams to create together manually.',
        icon: 'Sparkles',
        advantage: 'deckster',
      },
      {
        id: 'content-creation',
        title: 'Content Creation Approach',
        description: 'Deckster: AI writes your content. Pitch: Your team writes content collaboratively. Fundamentally different workflows.',
        icon: 'Users',
        advantage: 'deckster',
      },
      {
        id: 'team-features',
        title: 'Team Collaboration',
        description: 'Pitch excels with comments, approvals, version control, and real-time editing. Built for collaborative workflows.',
        icon: 'MessageSquare',
        advantage: 'competitor',
      },
      {
        id: 'templates',
        title: 'Template Quality',
        description: 'Pitch offers professional, designer-made templates. Both platforms have high-quality templates but serve different purposes.',
        icon: 'Layout',
        advantage: 'neutral',
      },
    ],
    featureComparison: [
      {
        id: 'ai-generation',
        feature: 'AI Content Generation',
        deckster: { value: true, description: 'Full AI creation', highlight: true },
        competitor: { value: false, description: 'Manual content creation' },
        category: 'ai',
      },
      {
        id: 'real-time-collab',
        feature: 'Real-Time Collaboration',
        deckster: { value: 'Coming soon', description: 'Planned feature' },
        competitor: { value: true, description: 'Full collaboration suite', highlight: true },
        category: 'collaboration',
      },
      {
        id: 'comments',
        feature: 'Comments & Approvals',
        deckster: { value: 'Planned', description: 'Future feature' },
        competitor: { value: true, description: 'Complete workflow', highlight: true },
        category: 'collaboration',
      },
      {
        id: 'version-control',
        feature: 'Version Control',
        deckster: { value: 'Basic', description: 'Version history' },
        competitor: { value: 'Advanced', description: 'Full version management', highlight: true },
        category: 'collaboration',
      },
      {
        id: 'template-quality',
        feature: 'Template Library',
        deckster: { value: '50+ AI-ready', description: 'AI-optimized templates' },
        competitor: { value: '100+ designer-made', description: 'Professional templates', highlight: true },
        category: 'core',
      },
      {
        id: 'ai-assistance',
        feature: 'AI Assistance',
        deckster: { value: 'Full AI team', description: '4 specialist agents', highlight: true },
        competitor: { value: 'None', description: 'No AI features' },
        category: 'ai',
      },
      {
        id: 'price',
        feature: 'Starting Price',
        deckster: { value: '$15/month', description: 'Individual plan' },
        competitor: { value: '$8/month', description: 'Per user', highlight: true },
        category: 'pricing',
      },
    ],
    useCaseComparisons: [
      {
        id: 'solo-creator',
        useCase: 'Creating Presentations Solo',
        decksterApproach: 'AI agents act as your virtual team, creating content, designs, and visualizations. Perfect for solo creators.',
        competitorApproach: 'Use professional templates and create content yourself. Good for individual users but no AI help.',
        winner: 'deckster',
      },
      {
        id: 'team-review',
        useCase: 'Team Review & Approval Process',
        decksterApproach: 'Export presentations for team review. Collaboration features coming soon.',
        competitorApproach: 'Built-in comments, approvals, version control. Designed specifically for team workflows.',
        winner: 'competitor',
      },
      {
        id: 'brand-decks',
        useCase: 'Branded Company Presentations',
        decksterApproach: 'AI generates on-brand content and designs. Consistent branding across all presentations.',
        competitorApproach: 'Create brand templates that team members use. Ensure consistency through templates and collaboration.',
        winner: 'tie',
      },
    ],
    verdict: {
      title: 'AI-First vs. Team-First',
      description: 'Pitch is perfect for teams that want to create presentations together with robust collaboration tools. Deckster is ideal for users who want AI to do the creative work.',
      bestFor: [
        'Teams working together on presentations',
        'Organizations with approval workflows',
        'Need for comments and version control',
        'Companies with multiple team members',
      ],
      decksterBestFor: [
        'Solo creators and small teams',
        'Users who want AI to create content',
        'Quick turnaround needed',
        'Focus on creation speed over collaboration',
      ],
    },
  },

  // PowerPoint Comparison
  {
    competitor: competitors[3],
    summary: {
      headline: 'PowerPoint, without the hours.',
      subheadline: 'Keep the PowerPoint export. Skip the blank slide, the formatting, the late night.',
      description: 'PowerPoint is the industry standard with decades of features. Deckster represents the next generation - built from the ground up for the AI era with specialized agents that transform how presentations are created.',
    },
    keyDifferentiators: [
      {
        id: 'ai-native',
        title: 'AI-Native Architecture',
        description: 'Deckster is built around AI agents from day one. PowerPoint has AI features added to a traditional tool. Fundamentally different approaches.',
        icon: 'Sparkles',
        advantage: 'deckster',
      },
      {
        id: 'creation-speed',
        title: 'Creation Speed',
        description: 'Deckster AI creates complete presentations in minutes. PowerPoint requires manual creation of every slide, though you can work faster with templates.',
        icon: 'Zap',
        advantage: 'deckster',
      },
      {
        id: 'features',
        title: 'Feature Breadth',
        description: 'PowerPoint has decades of features: animations, transitions, advanced formatting, presenter tools, and integration with Microsoft ecosystem.',
        icon: 'Package',
        advantage: 'competitor',
      },
      {
        id: 'familiarity',
        title: 'Industry Standard',
        description: 'PowerPoint is universally known and accepted. PPTX is the standard format. Deckster exports to PPTX for compatibility.',
        icon: 'Award',
        advantage: 'competitor',
      },
    ],
    featureComparison: [
      {
        id: 'ai-content',
        feature: 'AI Content Creation',
        deckster: { value: true, description: 'Full content generation', highlight: true },
        competitor: { value: 'Limited', description: 'Designer AI suggestions' },
        category: 'ai',
      },
      {
        id: 'ai-design',
        feature: 'AI Design Generation',
        deckster: { value: true, description: 'Custom AI layouts', highlight: true },
        competitor: { value: 'Partial', description: 'Design ideas feature' },
        category: 'ai',
      },
      {
        id: 'animations',
        feature: 'Animations & Transitions',
        deckster: { value: 'Basic', description: 'Essential animations' },
        competitor: { value: 'Advanced', description: 'Extensive options', highlight: true },
        category: 'core',
      },
      {
        id: 'presenter-tools',
        feature: 'Presenter Tools',
        deckster: { value: 'Basic', description: 'Speaker notes' },
        competitor: { value: 'Advanced', description: 'Presenter view, laser pointer', highlight: true },
        category: 'core',
      },
      {
        id: 'integrations',
        feature: 'Integrations',
        deckster: { value: 'Growing', description: 'Key integrations' },
        competitor: { value: 'Extensive', description: 'Microsoft 365 ecosystem', highlight: true },
        category: 'core',
      },
      {
        id: 'learning-curve',
        feature: 'Learning Curve',
        deckster: { value: 'Easy', description: 'Chat with AI', highlight: true },
        competitor: { value: 'Moderate', description: 'Many features to learn' },
        category: 'core',
      },
      {
        id: 'cost',
        feature: 'Pricing',
        deckster: { value: '$15/month', description: 'Standalone' },
        competitor: { value: '$6.99/month', description: 'With Microsoft 365', highlight: true },
        category: 'pricing',
      },
    ],
    useCaseComparisons: [
      {
        id: 'from-scratch',
        useCase: 'Creating Presentations from Scratch',
        decksterApproach: 'Describe your needs, AI creates complete presentation with content, design, and data visualizations in minutes.',
        competitorApproach: 'Start with blank slides or templates, manually create every element. Full control but time-intensive.',
        winner: 'deckster',
      },
      {
        id: 'advanced-animations',
        useCase: 'Complex Animations & Transitions',
        decksterApproach: 'Basic animations available. For advanced needs, export to PowerPoint.',
        competitorApproach: 'Industry-leading animation capabilities with precise control over every element.',
        winner: 'competitor',
      },
      {
        id: 'enterprise',
        useCase: 'Enterprise Deployment',
        decksterApproach: 'Modern web-based tool. Easy to adopt but limited enterprise features currently.',
        competitorApproach: 'Enterprise-grade with IT management, compliance, integration with corporate systems.',
        winner: 'competitor',
      },
    ],
    verdict: {
      title: 'Next Generation vs. Proven Standard',
      description: 'PowerPoint is the proven standard with unmatched features and universal acceptance. Deckster is the future - where AI does the creative work so you can focus on your message.',
      bestFor: [
        'Organizations standardized on Microsoft 365',
        'Need for advanced animations and effects',
        'Enterprise deployment requirements',
        'Complex presenter tool needs',
        'Maximum feature breadth',
      ],
      decksterBestFor: [
        'Want AI to create content and designs',
        'Need presentations fast',
        'Prefer modern, web-based tools',
        'Don\'t need advanced PowerPoint features',
        'Value creation speed over feature depth',
      ],
    },
  },
];

export const competitorsNavigation = competitors.map(comp => ({
  name: comp.name,
  href: `/compare/${comp.slug}`,
}));
